import { memo, useContext } from 'react';
import type { ReactNode } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';

import { SpinnerOverlay } from '../components/spinner';
import { Form, FormContext, ResetButton, SubmitButton } from '../components/forms';
import { Button } from '../components/buttons';

/** Пропсы внутреннего FormContent (кнопочная панель фильтра). */
type FormContentProps = {
  loading?: boolean;
  resetButtonTitle?: string;
  closeButtonTitle?: string;
  applyButtonTitle?: string;
  showResetButton?: boolean;
  showCloseButton?: boolean;
  showApplyButton?: boolean;
  disableCancelButton?: boolean;
  disableDeleteButton?: boolean;
  disableSubmitButton?: boolean;
  children?: ReactNode | ((formContext: any) => ReactNode);
  onClose?: (...args: unknown[]) => unknown;
  disableSubmitButtonFunction?: (values: any) => boolean;
};

const FormContent = (props: FormContentProps) => {
  // FormContext из forms/utils.js: при allowJs:false useContext возвращает unknown,
  // приводим к ожидаемой форме (Provider подаёт Formik-контекст).
  const formContext = useContext(FormContext) as {
    values: any;
    isSubmitting?: boolean;
    [key: string]: any;
  };
  const {
    loading,
    resetButtonTitle = 'Сбросить фильтр',
    closeButtonTitle = 'Закрыть фильтр',
    applyButtonTitle = 'Применить',
    showResetButton = true,
    showCloseButton = true,
    showApplyButton = true,
    disableCancelButton,
    disableDeleteButton,
    disableSubmitButton,
    children,
    onClose,
    disableSubmitButtonFunction,
  } = props;

  const disableSubmit = () => {
    if (disableSubmitButtonFunction) return disableSubmitButtonFunction(formContext?.values);
    return false;
  };

  return (
    <>
      { (typeof children === 'function') ? children(formContext) : children }

      <Row>
        <Col xs={ 12 }>
          <ButtonToolbar className='float-end'>
            { showApplyButton && (
              <SubmitButton
                text={ applyButtonTitle }
                disabled={ disableSubmitButton || formContext.isSubmitting || loading || disableSubmit() }
                variant='outline-primary'
              />
            ) }

            { showResetButton && (
              <ResetButton
                text={ resetButtonTitle }
                disabled={ disableCancelButton || formContext.isSubmitting || loading }
              />
            ) }

            { showCloseButton && (
              <Button
                text={ closeButtonTitle }
                disabled={ disableDeleteButton || formContext.isSubmitting || loading }
                onClick={ onClose }
                variant='outline-primary'
              />
            ) }
          </ButtonToolbar>
        </Col>
      </Row>
      <hr />
    </>
  );
};

/** Пропсы панели фильтров на базе Form. */
export interface FormFiltersProps {
  loading?: boolean;
  dontShowSpinner?: boolean;
  initialValues?: any;
  applyButtonTitle?: string;
  closeButtonTitle?: string;
  resetButtonTitle?: string;
  showApplyButton?: boolean;
  showCloseButton?: boolean;
  showResetButton?: boolean;
  disableCancelButton?: boolean;
  disableDeleteButton?: boolean;
  disableSubmitButton?: boolean;
  children?: ReactNode | ((formContext: any) => ReactNode);
  onReset?: (...args: unknown[]) => unknown;
  onClose?: (...args: unknown[]) => unknown;
  onApply?: (...args: unknown[]) => unknown;
  disableSubmitButtonFunction?: (values: any) => boolean;
}

const FormFilters = (props: FormFiltersProps) => {
  const {
    loading,
    initialValues,
    onReset,
    onApply,
    dontShowSpinner = false,
    ...rest
  } = props;

  const filterStyle = loading ? { opacity: '0.5' } : null;

  return (
    <>
      <div className='mt-3' style={ filterStyle }>
        <Form
          initialValues={ initialValues }
          onReset={ onReset }
          onSubmit={ onApply as any }
          enableReinitialize
        >
          <FormContent { ...rest } />
        </Form>
      </div>

      <SpinnerOverlay loading={ loading && !dontShowSpinner } />
    </>
  );
};

export default memo(FormFilters);
